import {
  AnimatePresence,
  type MotionProps,
  type Transition,
  type Variants,
  motion,
  useReducedMotion,
} from 'framer-motion';
import {
  useEffect,
  type PropsWithChildren,
  type ReactElement,
  Children,
  isValidElement,
} from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import { Flex } from '@dynatrace/strato-components/layouts';
import AnimationsJS from '@dynatrace/strato-design-tokens/animations-js';
import { XmarkIcon } from '@dynatrace/strato-icons';

import { useMicroguideContext } from './hooks/useMicroguideContext.js';
import {
  microguideAnimatedContentCSS,
  microguideContentWrapperCSS,
  microguideStepContainerCSS,
  microguideVisualWrapperCSS,
} from './Microguide.sty.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { Content } from '../shared-components/Content.js';
import { Title } from '../shared-components/Title.js';
import { Visual } from '../shared-components/Visual.js';

type MicroguideStepSlots = {
  title: ReactElement | null;
  visual: ReactElement | null;
  content: ReactElement | null;
};
//#endregion Typings

const contentVariants: Variants = {
  enter: (isNext: boolean) => {
    return {
      x: isNext ? '100%' : '-100%',
      opacity: 0,
    };
  },
  center: {
    x: 0,
    opacity: 1,
    position: 'relative',
  },
  exit: (isNext: boolean) => {
    return {
      x: isNext ? '-100%' : '100%',
      opacity: 0,
      position: 'absolute',
      left: 0,
    };
  },
};

const fadeProps: MotionProps = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    position: 'relative',
  },
  exit: {
    opacity: 0,
    position: 'absolute',
    left: 0,
  },
};

/**
 * Container for a single step of the Microguide.
 * @public
 */
export const Step = ({ children }: PropsWithChildren) => {
  const {
    onClose,
    currentStep,
    totalSteps,
    isNext,
    setHasContent,
    setHasTitle,
    closeButtonRef,
    closedOnKeydown,
  } = useMicroguideContext();
  const intl = useIntl();
  const slots: MicroguideStepSlots = {
    title: null,
    visual: null,
    content: null,
  };
  const shouldReduceMotion = useReducedMotion();
  const transitionProp: Transition = {
    transition: {
      duration: shouldReduceMotion ? 0 : AnimationsJS.SlideIn.Duration.Base,
      ease: AnimationsJS.Move.Easing,
    },
  };

  Children.forEach(children, (child) => {
    if (isSlot(child, Title)) {
      slots.title = child;
    } else if (isSlot(child, Visual)) {
      slots.visual = child;
    } else if (isSlot(child, Content)) {
      slots.content = child;
    } else if (isValidElement(child) && isDev()) {
      // eslint-disable-next-line no-console
      console.warn(
        'Only Title, Visual and Content are valid children of Microguide.Step.',
      );
    } else if (isDev()) {
      // eslint-disable-next-line no-console
      console.warn('The used child is not supported by Microguide.Step.');
    }
  });

  useEffect(() => {
    setHasTitle(slots.title !== undefined);
    setHasContent(slots.content !== undefined);
  }, [setHasContent, setHasTitle, slots.content, slots.title]);

  return (
    <Flex
      as={motion.div}
      flexDirection="column"
      gap={8}
      className={microguideStepContainerCSS}
    >
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={16}
        paddingTop={8}
        paddingLeft={16}
        paddingRight={8}
      >
        <Flex
          flexItem
          paddingY={6}
          flexGrow={1}
          id="strato-microguide-title"
          style={{ position: 'relative' }}
        >
          <AnimatePresence initial={false}>
            {slots.title && (
              <motion.div
                {...fadeProps}
                {...transitionProp}
                key={`strato-microguide-title-${currentStep}`}
              >
                {slots.title}
              </motion.div>
            )}
          </AnimatePresence>
        </Flex>
        <Button
          onClick={() => onClose(currentStep, currentStep + 1 === totalSteps)}
          onKeyDown={() => (closedOnKeydown.current = true)}
          ref={closeButtonRef}
          aria-label={intl.formatMessage({
            defaultMessage: 'Close microguide',
            id: '0yl3EugW5oCHVbO1',
            description: 'Close the microguide.',
          })}
        >
          <Button.Prefix>
            <XmarkIcon />
          </Button.Prefix>
        </Button>
      </Flex>
      {slots.visual && (
        <div className={microguideVisualWrapperCSS}>
          <AnimatePresence initial={false}>
            <motion.div
              {...fadeProps}
              {...transitionProp}
              key={`strato-microguide-visual-${currentStep}`}
            >
              {slots.visual}
            </motion.div>
          </AnimatePresence>
        </div>
      )}
      {slots.content && (
        <div
          className={microguideContentWrapperCSS}
          id="strato-microguide-content"
        >
          <AnimatePresence initial={false} custom={isNext}>
            <Flex
              as={motion.div}
              variants={contentVariants}
              className={microguideAnimatedContentCSS}
              custom={isNext}
              initial="enter"
              animate="center"
              exit="exit"
              {...transitionProp}
              key={`strato-microguide-content-${currentStep}`}
              flexDirection="column"
            >
              {slots.content}
            </Flex>
          </AnimatePresence>
        </div>
      )}
    </Flex>
  );
};

Step['displayName'] = 'Microguide.Step';
